/**
 * API client pentru import în lot (registry-import).
 */

import { apiClient } from './axios';
// Oglinda enum-urilor din schema root (imaginea frontend nu are acces la @prisma/client)
export type ImportStatus = 'QUEUED' | 'PROCESSING' | 'NEEDS_REVIEW' | 'APPROVED' | 'REJECTED' | 'FAILED';
export type ProgramType = 'DERMATO' | 'HEPATO' | 'BOTH';

// Tipuri identice cu backend (types.ts)
export interface ProposalBundle {
  identity: {
    cnp: string | null;
    firstName: string | null;
    lastName: string | null;
    gender: 'MALE' | 'FEMALE' | null;
    dateOfBirth: string | null; // YYYY-MM-DD
    address: string | null;
    city: string | null;
    county: string | null;
    matchedPatientId: string | null;
    allCnps: string[];
    allNames: string[];
  };
  documents: Record<string, DocumentProposal>;
}

export interface DocumentProposal {
  docType: string;
  engine: 'TESSERACT' | 'GEMINI' | 'NONE';
  confidence: number;
  missingRequired: string[];
  fields: Record<string, unknown>;
  evidence?: Record<string, { text: string; source: string }>;
  /** încredere per câmp (0-1) */
  fieldConfidence?: Record<string, number>;
  /** câmpurile completate de fallback-ul Gemini */
  geminiFields?: string[];
}

export interface Alert {
  code: string;
  severity: 'BLOCKING' | 'INFO';
  message: string;
  documentIds?: string[];
}

export interface ImportItemCorrections {
  identity?: Partial<ProposalBundle['identity']>;
  documents?: Record<string, { reclassify?: string; fields?: Record<string, unknown> }>;
}

export interface BatchUploadResult {
  batchId: string;
  totalItems: number;
  totalDocuments: number;
  items: Array<{
    id: string;
    folderName: string;
    documentCount: number;
  }>;
}

export interface DocumentWithDetails {
  id: string;
  fileName: string;
  relativePath: string;
  sha256: string;
  fileKey: string | null;
  mimeType: string;
  sizeBytes: number;
  docType: string | null;
  status: ImportStatus;
  engine: string | null;
  ocrText: string | null;
  pageCount: number | null;
  proposal: DocumentProposal | null;
  confidence: number | null;
  missingRequired: string[] | null;
  error: string | null;
  downloadUrl?: string;
}

export interface ItemWithDetails {
  id: string;
  batchId: string;
  folderName: string;
  status: ImportStatus;
  patientId: string | null;
  proposal: ProposalBundle | null;
  corrections: ImportItemCorrections | null;
  alerts: Alert[];
  blockingAlerts: number;
  reviewedBy: string | null;
  reviewedAt: Date | null;
  rejectReason: string | null;
  appliedAt: Date | null;
  applyResult: unknown;
  error: string | null;
  documents: DocumentWithDetails[];
}

export interface BatchSummary {
  id: string;
  label: string | null;
  programType: ProgramType;
  uploadedBy: string;
  uploadedAt: string;
  totalItems: number;
  totalDocuments: number;
  itemStats: {
    QUEUED: number;
    PROCESSING: number;
    NEEDS_REVIEW: number;
    APPROVED: number;
    REJECTED: number;
    FAILED: number;
  };
}

export interface ItemSummary {
  id: string;
  folderName: string;
  status: ImportStatus;
  documentCount: number;
  blockingAlerts: number;
  infoAlerts: number;
  patientId: string | null;
}

const unwrapResponse = <T>(responseData: any): T => responseData?.data ?? responseData;

export const registryImportApi = {
  /**
   * POST /api/registry-import/batches
   * Încarcă un pachet de documente (multipart).
   */
  async uploadBatch(
    files: File[],
    paths: string[],
    programType: ProgramType,
    label?: string
  ): Promise<BatchUploadResult> {
    const formData = new FormData();

    // Adaugă fișierele
    files.forEach((file) => {
      formData.append('files[]', file);
    });

    // Adaugă căile relative
    paths.forEach((path) => {
      formData.append('paths', path);
    });

    formData.append('programType', programType);
    if (label) {
      formData.append('label', label);
    }

    const response = await apiClient.post('/registry-import/batches', formData, {
      headers: { 'Content-Type': undefined },
      timeout: 300000, // 5 minute pentru upload mare
    });

    return unwrapResponse<BatchUploadResult>(response.data);
  },

  /**
   * GET /api/registry-import/batches
   * Lista loturilor utilizatorului.
   */
  async listBatches(): Promise<BatchSummary[]> {
    const response = await apiClient.get('/registry-import/batches');
    return unwrapResponse<BatchSummary[]>(response.data);
  },

  /**
   * GET /api/registry-import/batches/:id
   * Detalii lot.
   */
  async getBatch(batchId: string): Promise<BatchSummary> {
    const response = await apiClient.get(`/registry-import/batches/${batchId}`);
    return unwrapResponse<BatchSummary>(response.data);
  },

  /**
   * GET /api/registry-import/batches/:batchId/items
   * Lista item-urilor dintr-un lot.
   */
  async listItems(
    batchId: string,
    filters?: { status?: ImportStatus; blocking?: boolean }
  ): Promise<ItemSummary[]> {
    const response = await apiClient.get(`/registry-import/batches/${batchId}/items`, {
      params: filters,
    });
    return unwrapResponse<ItemSummary[]>(response.data);
  },

  /**
   * GET /api/registry-import/items/:id
   * Detalii item complet (cu propuneri, documente, alerte).
   */
  async getItem(itemId: string): Promise<ItemWithDetails> {
    const response = await apiClient.get(`/registry-import/items/${itemId}`);
    return unwrapResponse<ItemWithDetails>(response.data);
  },

  /**
   * PATCH /api/registry-import/items/:id
   * Salvează corecturi (identitate, reclasificări, câmpuri).
   */
  async patchItem(itemId: string, corrections: ImportItemCorrections): Promise<void> {
    await apiClient.patch(`/registry-import/items/${itemId}`, corrections);
  },

  /**
   * POST /api/registry-import/items/:id/approve
   * Aprobă item-ul (crează/actualizează pacientul, trimite la serviciu specialitate).
   */
  async approveItem(itemId: string): Promise<void> {
    const response = await apiClient.post(`/registry-import/items/${itemId}/approve`);
    return unwrapResponse<void>(response.data);
  },

  /**
   * POST /api/registry-import/items/:id/reprocess
   * Reia extracția documentelor (corecturile salvate se șterg).
   */
  async reprocessItem(itemId: string): Promise<void> {
    await apiClient.post(`/registry-import/items/${itemId}/reprocess`);
  },

  /**
   * POST /api/registry-import/items/:id/reject
   * Respinge item-ul cu motiv.
   */
  async rejectItem(itemId: string, reason: string): Promise<void> {
    const response = await apiClient.post(`/registry-import/items/${itemId}/reject`, { reason });
    return unwrapResponse<void>(response.data);
  },

  /**
   * GET /api/registry-import/documents/:id/file
   * Descarcă blob PDF (pentru iframe).
   */
  getDocumentFileUrl(documentId: string): string {
    return `${apiClient.defaults.baseURL}/registry-import/documents/${documentId}/file`;
  },

  /**
   * PDF-ul ca object URL (iframe-ul nu trimite token-ul de autentificare).
   * Apelantul eliberează URL-ul cu URL.revokeObjectURL.
   */
  async getDocumentBlobUrl(documentId: string): Promise<string> {
    const response = await apiClient.get(`/registry-import/documents/${documentId}/file`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  },
};
